import wrappedFetch from 'common/api/base';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
import { Plan } from '../plan';
import { getLoginWindow } from 'common/auth/modalLogin';
import subscribeChannel from 'common/api/subscribeChannel';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';

const baseURL = 'https://accounts.flattrade.in';

type PaymentStatus = {
  status: 'SUCCESS' | 'FAILURE' | string;
};

type PaymentSubscription = {
  unsubscribe: () => void;
};

class QubitPaymentService {
  private hsToken: string | null = null;
  private paymentSubscription: PaymentSubscription | null = null;
  private childWindow: Window | null = null;
  private brokerClientId: string;

  constructor() {
    const { SA_CONFIGS } = window;
    this.brokerClientId = SA_CONFIGS.currentBrokerDetails.current_broker_client_id;
  }

  async init({ reference_id, plan_name, price }: { reference_id: string; plan_name: string; price: number }) {
    const config = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reference_id }),
    };

    const res = await wrappedFetch('/qubit/purchases/init_payment', config);
    const data = await res.json();

    if (data.status !== 'success') {
      reactToastify('Failed to initialize payment. Please log in again.', TOASTIFY_TYPES.ERROR);
      throw new Error('Failed to initialize hsToken');
    }
    this.hsToken = data.hsTkn;

    this.buildAndOpenURL(reference_id, { plan_name, price });
  }

  buildAndOpenURL(reference_id: string, plan: Partial<Plan>) {
    if (!this.hsToken) {
      throw new Error('hsToken not initialized. Call init() first.');
    }

    const { price, plan_name } = plan;

    const params = new URLSearchParams({
      cid: this.brokerClientId,
      app: 'collection',
      src: String(ApplicationDomainUtility.partnerName),
      amt: String(price),
      rid: reference_id,
      key: this.hsToken,
      desc: String(plan_name || ''),
    });

    const url = `${baseURL}?${params.toString()}`;

    this.childWindow = getLoginWindow(url);

    this.subscribeToPaymentStatus({
      referenceId: reference_id,
      onReceived: (data) => {
        if (data.status === 'SUCCESS') {
          this.closeChildModal();
          reactToastify('Payment Successful.', TOASTIFY_TYPES.SUCCESS);

          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }
        if (data.status === 'FAILURE') {
          this.closeChildModal();
          reactToastify('Payment Failed.', TOASTIFY_TYPES.ERROR);
        }
      },
    });
  }

  closeChildModal() {
    if (this.childWindow && !this.childWindow.closed) {
      this.childWindow.close();
    }
    this.childWindow = null;
  }

  subscribeToPaymentStatus({
    referenceId,
    onReceived,
  }: {
    referenceId: string;
    onReceived: (data: PaymentStatus) => void;
  }) {
    if (!referenceId) return;

    if (this.paymentSubscription) {
      this.paymentSubscription.unsubscribe();
      this.paymentSubscription = null;
    }

    const subscription = subscribeChannel({
      roomId: `payment_status:${referenceId}_order`,
      onDataReceive: onReceived,
    }) as unknown as PaymentSubscription;

    this.paymentSubscription = subscription;
  }

  unsubscribe() {
    if (this.paymentSubscription) {
      this.paymentSubscription.unsubscribe();
      this.paymentSubscription = null;
    }
  }
}

export const QubitPaymentServices = new QubitPaymentService();
